<script setup lang="ts">
import { useResizeObserver } from "@vueuse/core";

const props = defineProps<{
  description: string;
}>();

const previewHeight = 260;
const contentId = useId();
const contentRef = ref<HTMLElement>();
const expanded = ref(false);
// Keep the server-rendered preview compact until its actual height is measured.
const overflowing = ref(true);

const measureContent = () => {
  if (contentRef.value) {
    overflowing.value = contentRef.value.scrollHeight > previewHeight;
  }
};

useResizeObserver(contentRef, measureContent);

watch(() => props.description, async () => {
  expanded.value = false;
  await nextTick();
  measureContent();
});
</script>

<template>
  <div class="category-description">
    <div
      :id="contentId"
      class="category-description__preview"
      :class="{
        'category-description__preview--collapsed': !expanded,
        'category-description__preview--faded': overflowing && !expanded,
      }"
      :style="{ '--category-description-preview-height': `${previewHeight}px` }"
      @focusin="expanded = true"
    >
      <div ref="contentRef" class="category-description__content" v-html="description" />
    </div>

    <div v-if="overflowing" class="d-flex justify-content-center mt-3">
      <el-button
        type="primary"
        plain
        round
        :aria-expanded="expanded"
        :aria-controls="contentId"
        @click="expanded = !expanded"
      >
        {{ $t(expanded ? 'hide_category_description' : 'show_full_category_description') }}
      </el-button>
    </div>
  </div>
</template>

<style scoped lang="scss">
.category-description__preview--collapsed {
  max-height: var(--category-description-preview-height);
  overflow: hidden;
}

.category-description__preview--faded {
  mask-image: linear-gradient(to bottom, #000 calc(100% - 100px), transparent calc(100% - 24px));
}

.category-description__content {
  // Include rich-text margins in the measured height and contain floated images.
  display: flow-root;
}
</style>
